import { useDeleteTitleOwnerDocumentMutation, useTitleOwnerDocumentPresignedUrlQuery } from '@graphql/index';
import { Box } from '@spark-web/box';
import { DocumentTextIcon, XIcon } from '@spark-web/icon';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';

type TitleOwnerDocumentLinkProps = {
  applicationId: string;
  titleSearchOwnerId: string;
  documentId: string;
  fileName: string;
  // Optimistic removal from the parent list, mirrored by onRestoreDocument on mutation failure.
  onRemoveDocument: (documentId: string) => void;
  onRestoreDocument: (documentId: string, fileName: string) => void;
};

// Title-owner analogue of UploadedDocumentsLink: shows an already-uploaded document as a
// download link (presigned GET URL) with an X that deletes it via deleteTitleOwnerDocument.
const TitleOwnerDocumentLink = ({
  applicationId,
  titleSearchOwnerId,
  documentId,
  fileName,
  onRemoveDocument,
  onRestoreDocument,
}: TitleOwnerDocumentLinkProps) => {
  const { data: downloadUrl } = useTitleOwnerDocumentPresignedUrlQuery(
    { input: { applicationId, titleSearchOwnerId, documentId } },
    {
      enabled: !!documentId,
      select: data => data.titleOwnerDocumentPresignedUrl,
    },
  );
  const { mutate } = useDeleteTitleOwnerDocumentMutation();

  const handleRemoveDocument = () => {
    // Optimistically drop it from the list, then delete server-side; restore on failure so
    // the UI never loses a document the backend still holds.
    onRemoveDocument(documentId);
    mutate(
      { input: { applicationId, titleSearchOwnerId, documentId } },
      {
        onError: () => {
          onRestoreDocument(documentId, fileName);
        },
      },
    );
  };

  if (!downloadUrl) {
    return null;
  }

  return (
    <Box border="field" borderRadius="small" padding="xsmall">
      <Box display="flex" alignItems="center" gap="medium" height="medium" paddingLeft="small">
        <DocumentTextIcon size="xsmall" />
        <Box flex={1}>
          <Text>
            <TextLink href={downloadUrl}>{fileName}</TextLink>
          </Text>
        </Box>
        <Box
          as="button"
          type="button"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="small"
          height="medium"
          width="medium"
          aria-label={`Remove ${fileName}`}
          onClick={handleRemoveDocument}
        >
          <XIcon size="xxsmall" />
        </Box>
      </Box>
    </Box>
  );
};

export default TitleOwnerDocumentLink;
